import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { usePopper } from 'react-popper'
import { useHistory } from 'react-router-dom'

import { Placement } from '@popperjs/core'
import cx from 'classnames'

import { Box, Flex, Text } from '@driftt/dds'
import { Button, H5 } from '@driftt/tide-core'
import { Drawer } from '@driftt/tide-drawer'
import { Icon } from '@driftt/tide-icons'

import { DeserializedSavedFilter } from 'api'
import AccountScoreTrendGraph from 'components/AccountScoreTrendGraph'
import CompanyLogo from 'components/CompanyLogo'
import { EngagementScoreFlames } from 'components/EngagementScoreFlames'
import EngagementScoreIndicator from 'components/EngagementScoreIndicator'
import { ExplainHoverCard } from 'components/Explain'
import FadeInAnimation from 'components/FadeInAnimation'
import Portal from 'components/Portal'
import { SortAndFilters } from 'components/SortAndFilter'
import {
  driftEmptyStateBuilder,
  driftErrorStateBuilder,
  driftLoadingStateBuilder
} from 'components/States'
import { InfiniteScrollTable } from 'components/TableView'
import TooltipBody from 'components/TooltipBody'
import { CompanyDetailsContext } from 'context/companyDetails'
import { CurrentUserContext } from 'context/currentUserContext'
import { OrgUsersById, OrgUsersContext } from 'context/orgUsersContext'
import { ScrollContext } from 'context/scrollContext'
import { SortContext } from 'context/sortContext'
import { useAccounts } from 'hooks/useAccounts'
import { useAttributeFilters } from 'hooks/useAttributeFilters'
import { useDebouncedCallback } from 'hooks/useDebouncedCallback'
import { useAppSelector } from 'hooks/useRedux'
import { TrafficControlAccess } from 'layouts/AccountActivity'
import {
  AccountActivity,
  AccountActivitySortValue,
  AIScoringTopActivities,
  ScoredActivity
} from 'modules/accountActivity/domain'
import withNotificationsModal from 'notifications/hooks/withNotificationsModal'
import { ReactComponent as SparklesIcon } from 'svgs/sparkles.svg'
import { prefixUrlWithHttp, userActivityTypeToWord } from 'utils/activity'
import { getEndUserDisplayInfo } from 'utils/endUser'
import { FilterableEntity, FilterGroup, FilterPage } from 'utils/filters'
import { defaultFilterTypes } from 'utils/filters'
import { accountPath, accountsPath } from 'utils/paths'
import { getTimestampActivityFormat } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import ScoreGraphTooltip from './ScoreGraphTooltip'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

const SCORE_GRAPH_COLUMNS = [
  {
    label: 'ACCOUNT',
    margin: '8px',
    sortable: true,
    sortValue: AccountActivitySortValue.ACCOUNT_NAME,
    initialDescending: false
  },
  {
    label: 'SCORE',
    sortable: true,
    sortValue: AccountActivitySortValue.ENGAGEMENT_SCORE
  },
  {
    label: 'TREND',
    sortable: true,
    sortValue: AccountActivitySortValue.TRENDING
  },
  {
    label: 'SCORE GRAPH',
    sortable: false,
    icon: <ScoreGraphTooltip />
  },
  {
    label: 'MOST RECENT ACTIVITY',
    sortable: true,
    sortValue: AccountActivitySortValue.MOST_RECENT
  }
]

declare global {
  interface Window {
    Bridge?: any
  }
}

const ACCOUNT_DETAILS_LINK_TRACKING_EVENT = 'ProspectorAccountDetailsAccountsLink'

const ActivityInfo = ({
  activity,
  topActivities = []
}: {
  activity: ScoredActivity | undefined
  topActivities?: AIScoringTopActivities[]
}) => {
  const orgUsersById = useContext<OrgUsersById>(OrgUsersContext)
  const { id } = activity || {}

  if (!activity) return null

  const isHightIntent = !!topActivities?.find(
    (a: AIScoringTopActivities) => a?.activityId === id && a.intent
  )

  return (
    <div className={styles.activityInfo} data-ocweb-id={ACCOUNT_DETAILS_LINK_TRACKING_EVENT}>
      {isHightIntent && (
        <Box
          css={{
            background: '$purple2',
            borderRadius: '$1',
            padding: '0 $half',
            width: 'fit-content',
            display: 'inline-flex',
            marginRight: '$half'
          }}>
          <Text variant="body">High Intent</Text>
        </Box>
      )}
      <span className={cx(styles.activityEndUser, utilityStyles.semiBoldWeight)}>
        <span className={styles.activityEndUserName}>{`${getEndUserDisplayInfo(activity)} `}</span>
      </span>

      <span className={styles.activityDetail}>
        <span>{`${userActivityTypeToWord(activity, orgUsersById)} `}</span>
        {activity.activityContext.pageUrl && (
          <a
            className={styles.activityLink}
            href={prefixUrlWithHttp(activity.activityContext.pageUrl)}
            target="_blank"
            rel="noreferrer noopener">
            {activity.activityContext.pageTitle ?? activity.activityContext.pageUrl}
          </a>
        )}
        {activity.activityContext.fromOutreach && <span>{` via Outreach`}</span>}
      </span>
      <div className={styles.activityInfoDate}>
        {getTimestampActivityFormat(activity.createdAt)}
      </div>
    </div>
  )
}

const accountInfo = (accountActivity: AccountActivity) => {
  return (
    <div className={styles.accountInfo} data-ocweb-id={ACCOUNT_DETAILS_LINK_TRACKING_EVENT}>
      <CompanyLogo company={accountActivity.companyData} />
      <div className={styles.nameAndDomain}>
        <div className={cx(styles.name, utilityStyles.semiBoldWeight, utilityStyles.mdFontSize)}>
          {accountActivity.account.name}
        </div>
        {!!accountActivity?.companyData?.domain && (
          <div className={styles.domain}>{`@${accountActivity.companyData.domain}`}</div>
        )}
      </div>
    </div>
  )
}

const scoreTrend = (accountActivity: AccountActivity) => {
  const currentScore = accountActivity.accountScoredActivity?.normalizedScore
  const historicalScores = accountActivity.accountScoredActivity?.historicalScores || []
  const lastScore = Math.min((historicalScores && historicalScores[7]) || 0, 5)

  const trend = Math.max(-5, Math.min(5, currentScore ?? 0) - lastScore)
  const direction = Math.abs(trend) < 1 ? 'neutral' : trend > 0 ? 'positive' : 'negative'
  return (
    <div
      className={cx(
        styles.scoreTrend,
        direction === 'neutral'
          ? styles.neutralTrend
          : direction === 'negative'
          ? styles.negativeTrend
          : styles.positiveTrend
      )}>
      <EngagementScoreIndicator variant={direction} />
      {direction !== 'neutral' && (
        <span
          className={cx(
            styles.scoreTrend,
            direction === 'negative' ? styles.negativeLabel : styles.positiveLabel
          )}>
          {trend > 0 ? '+' : ''}
          {trend.toFixed()}
        </span>
      )}
    </div>
  )
}

const mostRecentActivity = (accountActivity: AccountActivity) => {
  return (
    <ActivityInfo
      topActivities={accountActivity.accountScoredActivity?.topActivities}
      activity={accountActivity.mostRecentActivity}
    />
  )
}
export const EngagementScore = ({
  accountActivity,
  boundaryRef,
  placement,
  showMore
}: {
  accountActivity: AccountActivity
  boundaryRef: HTMLDivElement | null
  placement?: Placement
  showMore?: boolean
}) => {
  const [isShown, setIsShown] = useState(false)
  const currentUser = useContext(CurrentUserContext)
  const debouncedMouseEvent = useDebouncedCallback((toggle: boolean) => {
    setIsShown(toggle)
  }, 100)

  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const { styles: popperStyles, attributes } = usePopper(referenceElement, popperElement, {
    placement: placement ?? 'left-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [200, 15]
        }
      },
      {
        name: 'preventOverflow',
        options: {
          rootBoundary: 'document',
          boundary: boundaryRef ?? 'clippingParents',
          mainAxis: true,
          altAxis: false,
          padding: 0,
          altBoundary: true,
          tether: false
        }
      }
    ]
  })
  return (
    <div
      data-ocweb-id={ACCOUNT_DETAILS_LINK_TRACKING_EVENT}
      onMouseEnter={() => {
        const hoverEvent = accountActivity.accountScoredActivity
          ? TrackType.AI_ENGAGEMENT_SCORE_TOOLTIP_HOVER
          : TrackType.ENGAGEMENT_SCORE_TOOLTIP_HOVER
        setIsShown(true)
        debouncedMouseEvent(true)
        segmentTrack(hoverEvent, {
          orgId: currentUser?.orgId,
          accountId: accountActivity.accountId,
          userId: currentUser?.id
        })
      }}
      onMouseLeave={() => debouncedMouseEvent(false)}>
      <div ref={setReferenceElement}>
        {accountActivity && (
          <EngagementScoreFlames score={accountActivity.normalizedEngagementScore} />
        )}
      </div>
      {isShown && (
        <FadeInAnimation inProp={isShown}>
          <Portal>
            <div
              className={styles.popper}
              ref={setPopperElement}
              style={popperStyles.popper}
              {...attributes.popper}>
              <ExplainHoverCard accountActivity={accountActivity} limit={3} showMore={showMore} />
            </div>
          </Portal>
        </FadeInAnimation>
      )}
    </div>
  )
}

const noAccountsButtons = () => {
  return (
    <div className={styles.noAccountsButtonsContainer}>
      <Button
        href={'/settings/salesforce/config'}
        target="_blank"
        rel="noreferrer noopener"
        type="tertiary">
        Sync accounts from Salesforce
      </Button>
      <a
        className={styles.noAccountsTextStyle}
        href={'/accounts'}
        target="_blank"
        rel="noreferrer noopener">
        I’ll manually add accounts instead
      </a>
    </div>
  )
}

const errorComponent = driftErrorStateBuilder('your accounts')
const loadingComponent = driftLoadingStateBuilder('Loading accounts')
const emptyComponentFilters = driftEmptyStateBuilder(
  'We couldn’t find any accounts matching your filters.',
  [
    'Try removing or editing some filters to see accounts here. ',
    'Using Salesforce attribute filters? Updates to things like ',
    'account ownership can take 24 hours to be reflected in Drift.'
  ].join('')
)
const emptyComponentSearch = (element: React.ReactElement) =>
  driftEmptyStateBuilder(
    'We couldn’t find any accounts matching your search.',
    'Try refining your search terms to see accounts here.',
    element
  )

const emptyComponentNoFiltersAdmin = driftEmptyStateBuilder(
  'Get started by adding accounts for your team.',
  'Sync accounts from Salesforce to notify your team when a target account is on your site, get insights about your most engaged accounts, and ultimately close more deals.s',
  noAccountsButtons(),
  'cw-be-prepared',
  '1'
)

interface AccountsPageBodyProps {
  access: TrafficControlAccess
  onFilterChange?: () => void
  onClear?: () => void
  activeFilterId?: number
  onOpenEditModal?: (filter?: DeserializedSavedFilter, filterGroups?: FilterGroup[]) => void
}

const AccountsPage: React.FC<AccountsPageBodyProps> = ({
  access,
  onOpenEditModal,
  onFilterChange,
  onClear
}) => {
  const history = useHistory()
  const [showAccountHelpDrawer, setShowAccountHelpDrawer] = useState(false)

  const currentUser = useContext(CurrentUserContext)

  const isAdmin = currentUser?.role === 'ADMIN'

  const { searchQuery, filterGroupsByPage, activeFilterId } = useAppSelector(
    ({ filters }) => filters
  )
  const filterGroups = filterGroupsByPage[FilterPage.ACCOUNTS]
  const filters = filterGroups.reduce((flattened, group) => [...flattened, ...group], [])

  const accountOwners = filters?.find(item => item.filterType === FilterableEntity.ACCOUNT_OWNERS)
    ?.values

  const onRowClick = useCallback(
    (activity: AccountActivity) => {
      history.push({
        pathname: accountPath(activity.accountId),
        state: {
          accountData: activity.account,
          navigatedFrom: 'Target accounts',
          navigatedFromUrl: accountsPath,
          accountEngagementScore: activity.normalizedEngagementScore
        },
        hash: history.location.hash
      })
    },
    [history]
  )

  const { accountsSort, setAccountsSort, accountsDescending, setAccountsDescending } = useContext(
    SortContext
  )

  const { accountsScrollOffset, setAccountsScrollOffset } = useContext(ScrollContext)
  const scrollOffset = useRef(accountsScrollOffset)

  const { setCompanyData } = useContext(CompanyDetailsContext)
  setCompanyData(undefined)

  const {
    data,
    error,
    size,
    setSize,
    resultSize,
    isLoadingMore,
    isLoadingInitialData,
    hasLoadedInitialData,
    isReachingEnd
  } = useAccounts()

  const attributeFilters = useAttributeFilters()
  const containerRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)

  const containerWidth = containerRef.current?.clientWidth

  const hasUnifiedNav =
    currentUser?.gates?.has_unified_nav ||
    access.hasFeatures([{ featureName: 'prospector', accessLevel: 'READ' }])

  // Reset the scroll position when a search query or filter is applied.
  useEffect(() => {
    if (isLoadingInitialData) {
      scrollOffset.current = 0
      setAccountsScrollOffset(0)
    }
  }, [isLoadingInitialData, setAccountsScrollOffset])

  // Save the last scroll position on unmount.
  useEffect(() => {
    return () => {
      setAccountsScrollOffset(scrollOffset.current)
    }
  }, [setAccountsScrollOffset])

  const cellRenderers = useMemo(
    () => [
      accountInfo,
      (accountActivity: AccountActivity) => (
        <EngagementScore accountActivity={accountActivity} boundaryRef={ref.current} />
      ),
      scoreTrend,
      (accountActivity: AccountActivity) =>
        accountActivity.accountScoredActivity && (
          <AccountScoreTrendGraph score={accountActivity.accountScoredActivity} />
        ),
      mostRecentActivity
    ],

    []
  )
  const columns = useMemo(() => SCORE_GRAPH_COLUMNS, [])

  return (
    <div ref={ref} className={styles.listContainerSavedViews}>
      <Flex alignItems="center">
        <SparklesIcon />
        <Text>Engagement scores are determined by Drift AI</Text>
      </Flex>

      <div className={styles.activityFeedTableContainerSavedViews}>
        {attributeFilters ? (
          <SortAndFilters
            selectedSort={accountsSort}
            descending={accountsDescending}
            dataLength={resultSize ?? 0}
            singularName={'account'}
            pluralName={'accounts'}
            onClear={onClear}
            activeFilterId={activeFilterId}
            onOpenEditModal={onOpenEditModal}
            onFilterChange={onFilterChange}
            filterPage={FilterPage.ACCOUNTS}
            availableFilters={defaultFilterTypes}
            attributeFilters={attributeFilters}
            tooltip={
              <TooltipBody size="large">
                Only accounts with activity since your admin first synced them to Drift are
                displayed here.
              </TooltipBody>
            }
          />
        ) : (
          <div className={styles.filtersLoading} />
        )}
        <div ref={containerRef} className={cx(styles.tableContainer, styles.tableContainerScore)}>
          <div
            className={cx(styles.subTableContainer, styles.subTableContainerSavedViews)}
            style={{
              height:
                data && data.length > 0
                  ? `calc(min(100% - 50px, ${56 * data.length}px) + 32px)`
                  : '100%'
            }}>
            <InfiniteScrollTable
              rowHeight={56}
              rowClassName={styles.rowStructureStyleGraph}
              columns={columns}
              resultSize={resultSize ?? 0}
              data={data}
              loadMore={() => {
                !isReachingEnd &&
                  !isLoadingMore &&
                  !isLoadingInitialData &&
                  setSize((size ?? 0) + 1)
              }}
              cellRenderers={cellRenderers}
              onRowClick={onRowClick}
              error={error}
              errorComponent={errorComponent}
              loadingComponent={loadingComponent}
              emptyComponent={
                isAdmin &&
                (!searchQuery || (searchQuery && searchQuery.length === 0)) &&
                accountOwners?.length === 0
                  ? emptyComponentNoFiltersAdmin
                  : !searchQuery || (searchQuery && searchQuery.length === 0)
                  ? emptyComponentFilters
                  : emptyComponentSearch(
                      <button
                        className={styles.accountsHelpButton}
                        onClick={() => setShowAccountHelpDrawer(true)}>
                        <Icon
                          className={styles.boltIcon}
                          name="lightning-bolt-filled"
                          width={17}
                          height={20}
                        />
                        Why aren’t all of my accounts displayed here?
                      </button>
                    )
              }
              isLoadingInitialData={isLoadingInitialData}
              initialDataLoaded={hasLoadedInitialData}
              selectedSort={accountsSort}
              onSortByChange={setAccountsSort}
              descending={accountsDescending}
              onSortDirChange={setAccountsDescending}
              initialScrollOffset={accountsScrollOffset}
              onScrollOffsetChange={offset => {
                scrollOffset.current = offset
              }}
              listStyle={
                containerWidth
                  ? {
                      overflowY: 'scroll',
                      overflowX: 'hidden',
                      width: containerWidth > 1502 ? containerWidth : 1502,
                      direction: 'rtl',
                      paddingBottom: 4
                    }
                  : {}
              }
            />
          </div>
        </div>
      </div>
      <Drawer
        className={cx(styles.drawer, {
          [styles.unified]: hasUnifiedNav,
          [styles.desktop]: !!window.Bridge
        })}
        containerClassName={styles.drawerContainer}
        isOpen={showAccountHelpDrawer}
        closeDrawer={() => setShowAccountHelpDrawer(false)}>
        <div className={styles.drawerContainerInner}>
          <H5 className={styles.drawerHeader}>
            <Icon className={styles.boltIcon} name="lightning-bolt-filled" width={17} height={20} />
            Why aren’t all of my accounts displayed here?
          </H5>
          <p>
            Only accounts with activity since your admin first synced them to Drift are displayed
            here. You may not see all of the accounts that you own if they have not been active
            (visiting your site, engaging with emails, etc) during that period.
          </p>
        </div>
      </Drawer>
    </div>
  )
}

export default withNotificationsModal(AccountsPage)
